import { useParams } from 'wouter';
import { useState } from 'react';

/**
 * GCS bucket URL for ticket card images.
 */
const GCS_TICKET_IMAGES_BUCKET =
  import.meta.env.VITE_TICKET_IMAGES_GCS_BUCKET || 'mangelapp-uploads-eu';
const GCS_TICKET_IMAGES_URL = `https://storage.googleapis.com/${GCS_TICKET_IMAGES_BUCKET}/ticket_images`;

/**
 * Public ticket view that displays the generated ticket card image.
 * 
 * This page shows the same image that was sent via WhatsApp.
 * The image is stored in GCS with a deterministic filename: ticket-{ticketId}.png
 */
export default function PublicTicketView() {
  const { id } = useParams<{ id: string }>();
  const [imageError, setImageError] = useState(false);

  // Construct the image URL directly - no API call needed
  const imageUrl = id ? `${GCS_TICKET_IMAGES_URL}/ticket-${id}.png` : null;

  if (!id) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Ticket Not Found</h1>
          <p className="text-gray-600">The ticket you're looking for doesn't exist or is no longer available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        <div className="mb-4 flex w-full flex-col items-center">
          <h1 className="w-full text-center text-xl font-semibold text-gray-900">Ticket Details</h1>
        </div>
        
        {imageError ? (
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <p className="text-gray-600">Unable to load ticket image</p>
            <p className="mt-2 text-sm text-gray-500">The ticket image may not be available yet.</p>
          </div>
        ) : (
          <div className="rounded-lg overflow-hidden shadow-lg">
            <img 
              src={imageUrl!} 
              alt="Ticket"
              className="w-full h-auto"
              loading="eager"
              onError={() => setImageError(true)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
